/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  HeartOutlined,
  MessageOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Card,
  Col,
  List,
  message,
  Modal,
  Row,
  Spin,
  Table,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { TwitterTweetEmbed } from "react-twitter-embed";

import { getSupaClient } from "../../lib/supabase";
import fetchTweets from "../../utils/tweets";

// Individual tweet component to embed the tweet. Doesn't use the api
const IndividualTweet = (props) => {
  useLayoutEffect(() => {}, [props.id]);
  return (
    <div>
      <TwitterTweetEmbed tweetId={props.id} key={props.id} />
    </div>
  );
};

// props: startTime: ISO string to denote the start time of tweet pull
// endTime: ISO string
const TweetView = (props) => {
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const supabase = getSupaClient();
  const [tweetArray, setTweetArray] = useState([]);
  const [threadMessages, setThreadMessages] = useState([]);
  const [isPreviewVisible, setPreviewVisible] = useState(false);
  const [previewMessage, setPreviewMessage] = useState({});
  const [includesArray, setIncludesArray] = useState([]);
  const [currentTweet, setCurrentTweet] = useState({});
  const [twitterIdVal, setTwitterIdVal] = useState(null);

  const teamId = localStorage.getItem("team-id");

  // Fetch data from the lambda function
  const loadPageData = async (twitterIds) => {
    const tweets = await Promise.all(
      twitterIds.map((twitterId) => {
        return fetchTweets({
          twitterUserId: twitterId,
          startTime: dayjs(props.startTime).toISOString(),
          endTime: dayjs(props.endTime).toISOString(),
        });
      })
    );
    console.log("tweets ", tweets);
    let tweetArrayTemp = [];
    if (tweets[0].error) {
      message.error("Error loading tweets. Please contact support");
    } else {
      // combine the array if multiple tweets
      tweets.forEach((tweet) => {
        if (tweets.error) {
          message.error("Error loading tweets. Please try again");
        } else {
          tweetArrayTemp = tweetArrayTemp.concat(tweet);
        }
      });
      // console.log("combined", tweetArrayTemp);
    }
    setTweetArray(tweetArrayTemp);
    setLoading(false);
  };

  const loadTeamSpecificData = async () => {
    // check if local storage exists
    const twitterIds = await JSON.parse(localStorage.getItem("twitter-ids"));
    console.log("running team ", typeof twitterId);
    // check local first
    if (twitterIds) {
      setTwitterIdVal(twitterIds);
      loadPageData(twitterIds);
    }
    // otherwise grab it from backend and set localstorage for team id
    else {
      console.log("grabbing twitter id");
      const { data, error } = await supabase
        .from("teams")
        .select("twitter_ids")
        .match({ slack_id: teamId });
      if (data) {
        // console.log("twitter data2", data);
        if (data[0].twitter_ids) {
          loadPageData(data[0].twitter_ids);
          localStorage.setItem(
            "twitter-ids",
            JSON.stringify(data[0].twitter_ids)
          );
          setTwitterIdVal(data[0].twitter_ids);
        } else {
          setLoading(false);
          console.log("No twitter set");
          message.info("No twitter set. Please contact support");
        }
      }
      if (error) {
        console.log("error", error);
      }
    }
  };

  useEffect(async () => {
    // get twitter user id and then the data
    loadTeamSpecificData();
    // document.title = "Tweets | Dots";
  }, [props]);

  // show modal to look at a tweet in detail
  const showPreview = async (tweetId) => {
    message.loading("Pulling info", 0.7);
    await setCurrentTweet(tweetId);
    setPreviewVisible(true);
  };

  // close the individual tweet modal
  const clearPreview = () => {
    setPreviewMessage({});
    setPreviewVisible(false);
  };

  // column config for tweets
  const twitterColumns = [
    {
      title: "Posted",
      dataIndex: "created_at",
      render: (text, record) =>
        dayjs(record.tweet.created_at).format("DD-MMM, HH:mm"),
      sorter: (a, b) => {
        console.log(a, b, dayjs(a.tweet.created_at).unix());
        return (
          dayjs(a.tweet.created_at).unix() - dayjs(b.tweet.created_at).unix()
        );
      },
      width: "10%",
    },
    {
      title: "Tweet",
      dataIndex: "text",
      key: "text",
      render: (text, record) => (
        <div>
          <div className="flex flex-row">
            <div className="mx-3 pt-2">
              <Tooltip title={`Click to open ${record.user.name}'s profile`}>
                <a
                  href={`https://twitter.com/${record.user.username}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Avatar
                    src={
                      record.user.profile_image_url
                        ? record.user.profile_image_url
                        : null
                    }
                  />
                </a>
              </Tooltip>
            </div>
            <div>
              <div className="flex flex-col flex-grow">
                <div className="flex flex-row">
                  <span style={{ color: "grey", marginRight: "10px" }}>
                    {`${record.user.name} (@${record.user.username})`}
                  </span>
                  <Tooltip title="Click to open in twitter">
                    <a
                      href={`https://twitter.com/${record.user.username}/status/${record.tweet.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        alt="Link"
                        width="16"
                        className="pt-0.5"
                        src="/external-link.svg"
                      />
                    </a>
                  </Tooltip>
                </div>
                <div>
                  <a
                    onKeyDown={() => showPreview(record.tweet.id)}
                    onClick={() => showPreview(record.tweet.id)}
                    role="button"
                    tabIndex={0}
                  >
                    {record.tweet.text}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      width: "80%",
    },
    {
      title: "Engagement",
      dataIndex: "metrics",
      render: (text, record) => (
        <div className="space-x-2 flex  justify-evenly">
          {record.tweet.public_metrics.like_count ? (
            <div>
              <span className="pt-4">
                {record.tweet.public_metrics.like_count}
              </span>{" "}
              <HeartOutlined />
            </div>
          ) : null}
          {record.tweet.public_metrics.reply_count ? (
            <div>
              {record.tweet.public_metrics.reply_count} <MessageOutlined />
            </div>
          ) : null}

          {record.tweet.public_metrics.retweet_count ? (
            <div>
              {record.tweet.public_metrics.retweet_count} <RetweetOutlined />
            </div>
          ) : null}
          {record.tweet.public_metrics.quote_count ? (
            <div className="flex flex-row">
              {" "}
              {record.tweet.public_metrics.quote_count}{" "}
              <img
                alt="quotes"
                className="ml-1 mt-1"
                src="/right-quote.png"
                width="13"
              />
            </div>
          ) : null}
        </div>
      ),
      width: "10%",
    },
  ];

  return (
    <div className="w-full flex-shrink">
      <Spin spinning={loading}>
        {!twitterIdVal ? (
          <p>No twitter account set. Please contact support to set one up! </p>
        ) : null}

        <Row justify="spaced-around" gutter={[32, 32]}>
          <Col lg={24} md={24}>
            <Card bordered={false}>
              <div style={{ width: "100%", height: "100%" }}>
                {twitterIdVal ? (
                  <Table
                    dataSource={tweetArray}
                    columns={twitterColumns}
                    rowKey={(record) => record.tweet.id}
                  />
                ) : null}
              </div>
            </Card>
          </Col>
        </Row>
        <Modal visible={isPreviewVisible} onCancel={clearPreview} footer={null}>
          <p>Full tweet with responses</p>
          <IndividualTweet id={currentTweet} />
        </Modal>
      </Spin>
    </div>
  );
};

export default TweetView;
